
import React from 'react';
import { motion } from 'framer-motion';
import { Terminal, Cpu, Shield, Zap, Search, Activity, Globe, Lock } from 'lucide-react';
import { cn } from '../lib/utils';

export const EventLogWidget = () => {
  const logs = [
    { id: 1, text: 'agent.ocr.extract → done', color: 'text-accent' },
    { id: 2, text: 'agent.classify.route → contract', color: 'text-gold' },
    { id: 3, text: 'agent.translate.ar_en → done', color: 'text-blue-400' },
    { id: 4, text: 'agent.audit.append → ok', color: 'text-green-400' },
  ];

  return (
    <div className="bg-deep-0/80 backdrop-blur-md border border-white/10 rounded-xl p-4 font-mono text-[10px] space-y-1.5 shadow-2xl w-48">
      <div className="flex items-center gap-2 mb-2 border-b border-white/5 pb-1">
        <Terminal className="w-3 h-3 text-white/40" />
        <span className="text-white/40 uppercase tracking-widest">Live Mission Log</span>
      </div>
      {logs.map((log, i) => (
        <motion.div
          key={log.id}
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.5, repeat: Infinity, repeatDelay: 2 }}
          className={cn("flex items-center gap-2", log.color)}
        >
          <span className="opacity-40">{'>>>'}</span>
          <span>{log.text}</span>
        </motion.div>
      ))}
      <motion.div
        animate={{ opacity: [1, 0] }}
        transition={{ repeat: Infinity, duration: 0.8 }}
        className="w-1.5 h-3 bg-accent"
      />
    </div>
  );
};

export const HolographicDocScan = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative w-40 h-52 bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden backdrop-blur-sm group"
    >
      <div className="p-3 space-y-2">
        <div className="w-full h-1 bg-white/10 rounded" />
        <div className="w-3/4 h-1 bg-white/10 rounded" />
        <div className="w-full h-12 bg-white/5 rounded-md flex items-center justify-center">
          <Globe className="w-4 h-4 text-white/20" />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <div className="h-4 bg-accent/20 rounded border border-accent/30" />
          <div className="h-4 bg-gold/20 rounded border border-gold/30" />
        </div>
      </div>
      {/* Scan Line */}
      <motion.div
        animate={{ top: ['0%', '100%', '0%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
        className="absolute left-0 w-full h-0.5 bg-accent shadow-[0_0_15px_#F76C1C] z-10"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.div>
  );
};

export const AgentCoreVisual = () => {
  const labels = [
    { icon: Search, text: 'OCR', pos: 'top-0 left-1/2 -translate-x-1/2' },
    { icon: Activity, text: 'CONFIDENCE', pos: 'top-1/4 right-8' },
    { icon: Shield, text: 'POLICY', pos: 'bottom-1/4 right-8' },
    { icon: Lock, text: 'AUDIT', pos: 'bottom-0 left-1/2 -translate-x-1/2' },
    { icon: Zap, text: 'ROUTING', pos: 'bottom-1/4 left-8' },
    { icon: Cpu, text: 'AGENT_CORE', pos: 'top-1/4 left-8' },
  ];

  return (
    <div className="relative w-full h-[600px] flex items-center justify-center select-none">
      {/* Background Grid/Mesh */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--accent)_0%,_transparent_70%)] opacity-10" />
        <div className="w-full h-full border-[0.5px] border-white/5 [mask-image:radial-gradient(ellipse_at_center,black,transparent)]" />
      </div>

      <div className="radar-sweep opacity-20 scale-75" />

      {/* Central Orb Container */}
      <div className="relative w-80 h-80 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-0 border border-white/5 rounded-full"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-4 border border-dashed border-accent/20 rounded-full"
        />

        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="w-48 h-48 rounded-full bg-gradient-to-br from-accent via-gold to-accent/50 opacity-20 blur-3xl"
        />
        
        <div className="absolute w-32 h-32 rounded-full bg-deep-1 border border-accent/40 shadow-[0_0_50px_rgba(247,108,28,0.3)] flex items-center justify-center overflow-hidden z-10">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" />
          <Cpu className="w-12 h-12 text-accent animate-pulse" />
        </div>

        {/* Floating Labels */}
        {labels.map((item, i) => (
          <motion.div
            key={item.text}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.2 }}
            className={cn("absolute flex flex-col items-center gap-1 z-20", item.pos)}
          >
            <div className="p-2 rounded-lg bg-deep-surface border border-white/10 shadow-xl backdrop-blur-md">
              <item.icon className="w-4 h-4 text-accent" />
            </div>
            <span className="text-[8px] font-black tracking-[0.2em] text-white/40 uppercase whitespace-nowrap">{item.text}</span>
          </motion.div>
        ))}
      </div>

      {/* Side Widgets - REPOSITIONED */}
      {/* Live Mission Log: Kept at top right */}
      {/* <div className="absolute top-5 right-0 lg:-right-12 z-30">
        <EventLogWidget />
      </div> */}

      {/* Holographic Doc Scan: Moved significantly down and right to avoid hero text */}
      {/* <div className="absolute bottom-5 right-10 lg:right-20 z-30 scale-90 origin-bottom-right">
        <HolographicDocScan />
      </div> */}

      {/* Telemetry Dots */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-accent rounded-full"
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 1, 0],
            x: Math.sin(i) * 300,
            y: Math.cos(i) * 300,
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: i * 0.4
          }}
        />
      ))}
    </div>
  );
};